import React from "react";

import type { LedgerImportRowDTO } from "../../data/ledgerImportApi";
import { explainRejection } from "../../domain/ledgerImport";
import { Callout } from "../../components/Callout";

/**
 * The rows that did not make it, on their own and at the top (ZOUR-257).
 *
 * They used to ride inside the "Added" group with an amber badge, rows 67–73 of
 * a scrolled list under a heading that said every row had been added. A CA
 * reading the result needs exactly these lines and nothing else, so they get a
 * block of their own: always open, never in a scroll box, each line naming the
 * sheet row so the fix is a click away in Excel.
 */
export function LedgerImportRejected({
  rows,
  notDone,
}: {
  rows: LedgerImportRowDTO[];
  /** "were not added" and friends, for this many rows — see pushVerb. */
  notDone: (count: number) => string;
}) {
  if (rows.length === 0) return null;

  // Only say Zoho rejected them when it did. A skipped row never reached Zoho;
  // its parent did not arrive.
  const allZoho = rows.every((row) => row.status === "failed");
  const heading = `${rows.length} ${rows.length === 1 ? "ledger" : "ledgers"} ${notDone(rows.length)}`;

  return (
    <Callout variant="warning">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="font-medium text-foreground">{heading}</p>
          <p>
            {allZoho
              ? "Zoho rejected these. Fix them in the sheet and upload it again."
              : "Fix these in the sheet and upload it again."}
          </p>
        </div>

        <ul className="flex flex-col divide-y divide-amber-500/20">
          {rows.map((row) => {
            const { reason, zohoMessage, code } = explainRejection(row.error, row.parent_name);

            return (
              <li key={row.id} className="flex min-w-0 flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
                <p className="break-words text-foreground">
                  <span className="font-mono text-xs text-muted-foreground">Row {row.sheet_row_no}</span>
                  <span aria-hidden="true" className="text-muted-foreground">
                    {" · "}
                  </span>
                  <span className="font-medium">{row.account_name ?? "(no name)"}</span>
                  {row.parent_name !== null && (
                    <span className="text-muted-foreground"> · under {row.parent_name}</span>
                  )}
                </p>

                {/* No dark: variant. The onboarding theme is light-only (styles.css),
                    so amber-400 under an OS dark preference lands on a white page. */}
                <p className="break-words text-amber-700">
                  {reason}
                  {zohoMessage === null && code !== null && (
                    <span className="text-xs text-muted-foreground"> (code {code})</span>
                  )}
                </p>

                {zohoMessage !== null && (
                  <p className="break-words text-xs text-muted-foreground">
                    Zoho's message: {zohoMessage}
                    {code !== null && ` (code ${code})`}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </Callout>
  );
}
